import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { reportsService, storageService } from "@/services";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Camera,
  Loader2,
  X,
  Upload,
  AlertCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { captureEvent } from "@/lib/analytics";

const internalCategories = [
  { value: "electrical", label: "Electrical ⚡", description: "Electrical issues and repairs" },
  { value: "plumbing", label: "Plumbing 🔧", description: "Plumbing and water issues" },
  { value: "security", label: "Security 🔐", description: "Security and access control" },
  { value: "general_maintenance", label: "General Maintenance 🛠️", description: "General estate maintenance" },
  { value: "garden_landscape", label: "Garden/Landscape 🌿", description: "Garden and landscaping" },
  { value: "structural", label: "Structural 🏗️", description: "Building structure issues" },
  { value: "other", label: "Other 📋", description: "Other internal maintenance" },
];

const assignmentOptions = [
  { value: "unassigned", label: "Unassigned" },
  { value: "self", label: "Self" },
  { value: "contractor", label: "Contractor" },
  { value: "staff", label: "Staff" },
];

export default function InternalIssueForm({ isOpen, onClose, estateId }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    assigned_to: "unassigned",
  });

  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const createInternalIssueMutation = useMutation({
    mutationFn: async (issueData) => {
      // Upload photos first if any
      let photoUrls = [];
      if (photos.length > 0) {
        setUploadingPhotos(true);
        try {
          photoUrls = await Promise.all(
            photos.map(async (photo) => {
              const result = await storageService.uploadFile(photo, 'report-photos');
              return result.file_url; // Extract just the URL
            })
          );
        } catch (error) {
          console.error('Photo upload failed:', error);
          toast.error('Failed to upload photos');
          throw error;
        } finally {
          setUploadingPhotos(false);
        }
      }

      // Create the internal issue
      const reportData = {
        ...issueData,
        photo_urls: photoUrls,
        created_by: user.id,
        created_by_email: user.email,
        status: 'reported',
        is_internal: true,
        estate_id: estateId,
        // Convert "unassigned" to null for database storage
        assigned_to: issueData.assigned_to === 'unassigned' ? null : issueData.assigned_to,
        // Set coordinates to estate center (placeholder - could be made more specific)
        latitude: 0,
        longitude: 0,
      };

      const result = await reportsService.create(reportData);
      return result;
    },
    onSuccess: (createdIssue) => {
      captureEvent("internal_issue_created", {
        issue_id: createdIssue?.id,
        category: createdIssue?.category || formData.category,
        assigned_to: createdIssue?.assigned_to || formData.assigned_to,
        photos_count: createdIssue?.photo_urls?.length || photos.length,
      });
      toast.success('Internal issue created successfully');
      queryClient.invalidateQueries({ queryKey: ['estate-reports'] });
      queryClient.invalidateQueries({ queryKey: ['estate-stats'] });
      queryClient.invalidateQueries({ queryKey: ['internal-issues'] });
      queryClient.invalidateQueries({ queryKey: ['internal-issues-stats'] });
      handleClose();
    },
    onError: (error) => {
      console.error('Failed to create internal issue:', error);
      toast.error('Failed to create internal issue');
    },
  });

  const handleClose = () => {
    setFormData({
      title: "",
      description: "",
      category: "",
      assigned_to: "unassigned",
    });
    setPhotos([]);
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoSelect = (event) => {
    const files = Array.from(event.target.files);
    if (photos.length + files.length > 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }
    
    setPhotos(prev => [...prev, ...files]);
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    
    if (!formData.category) {
      toast.error('Category is required');
      return;
    }

    createInternalIssueMutation.mutate(formData);
  };

  const isLoading = createInternalIssueMutation.isPending || uploadingPhotos;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-orange-600" />
            Create Internal Issue
          </DialogTitle>
          <DialogDescription>
            Create a new internal maintenance issue for estate management.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Issue Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Brief description of the issue"
              disabled={isLoading}
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>Category *</Label>
            <Select 
              value={formData.category} 
              onValueChange={(value) => handleInputChange('category', value)}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select issue category" />
              </SelectTrigger>
              <SelectContent>
                {internalCategories.map((category) => (
                  <SelectItem key={category.value} value={category.value}>
                    <div>
                      <div className="font-medium">{category.label}</div>
                      <div className="text-sm text-muted-foreground">{category.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Detailed description of the issue and any specific requirements"
              rows={4}
              disabled={isLoading}
            />
          </div>

          {/* Assign To */}
          <div className="space-y-2">
            <Label>Assign To</Label>
            <Select 
              value={formData.assigned_to} 
              onValueChange={(value) => handleInputChange('assigned_to', value)}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select assignment (optional)" />
              </SelectTrigger>
              <SelectContent>
                {assignmentOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Photo Upload */}
          <div className="space-y-3">
            <Label>Photos (Optional)</Label>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <Camera className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <div className="text-sm text-gray-600 mb-2">
                  Upload photos to help document the issue
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoSelect}
                  className="hidden"
                  id="photo-upload"
                  disabled={isLoading}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('photo-upload').click()}
                  disabled={isLoading || photos.length >= 5}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose Photos
                </Button>
                <div className="text-xs text-gray-500 mt-1">
                  Maximum 5 photos, up to 10MB each
                </div>
              </div>
            </div>

            {/* Photo Preview */}
            {photos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <AnimatePresence>
                  {photos.map((photo, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="relative group"
                    >
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={isLoading}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Issue'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}